import { memo, useEffect, useMemo, useState, type CSSProperties } from "react";
import type { TFunction } from "i18next";
import { useGetVideoViewSolution } from "@oneapp/data";
import { Icon } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { MathJaxHtml } from "../../../../../components/mathJax/MathJaxHtml";
import type { QuestionData } from "../models/GetQuestions";
import styles from "./ReviseSolutionWeb.module.scss";

export type ReviseSolutionVideoPlayerPayload = {
  videoId: number;
  title: string;
  thumbnail: string;
  videoURL: string;
  duration: string;
  description: string;
  typeVideoTypeName: string;
  mediaTypeName: string;
};

type SolutionVideoState = ReviseSolutionVideoPlayerPayload;

export type ReviseSolutionWebVariant = "overlay" | "drawer";

export type ReviseSolutionSessionStats = {
  accuracyPercent: number;
  attemptCount: number;
};

type Props = {
  t: TFunction<"selflearn">;
  questionData: QuestionData;
  onBack: () => void;
  onLikePress: () => void;
  onDislikePress: () => void;
  onOpenReportModal: (assetName: string, feedBack: boolean) => void;
  videoId: number;
  onOpenVideo: (payload: ReviseSolutionVideoPlayerPayload) => void;
  variant?: ReviseSolutionWebVariant;
  solutionSessionStats?: ReviseSolutionSessionStats | null;
};

const ReviseSolutionWebInner = ({
  t,
  questionData,
  onBack,
  onLikePress,
  onDislikePress,
  onOpenReportModal,
  videoId,
  onOpenVideo,
  variant = "overlay",
}: Props) => {
  const { theme } = useTheme();
  const [video, setVideo] = useState<SolutionVideoState | null>(null);

  const titleLarge = theme.typography.title.large.regular;
  const labelSmall = theme.typography.label.small.regular;
  const bodyMedium = theme.typography.body.medium.regular;
  const bodyLarge = theme.typography.body.large.regular;
  const headlineMedium = theme.typography.headline.medium.regular;
  const titleMedium = theme.typography.title.medium.regular;

  const rootStyle = useMemo(
    () =>
      ({
        "--rq-solution-bg": theme.colors.secondary[90],
        "--rq-solution-panel-border": theme.colors.secondary[50],
        "--rq-solution-card": theme.colors.neutral[100],
        "--rq-solution-title-accent": theme.colors.secondary[10],
        "--rq-solution-muted": theme.colors.neutral[60],
        "--rq-solution-muted-strong": theme.colors.neutral[50],
        "--rq-solution-icon-muted": theme.colors.neutral[20],
        "--rq-primary-40": theme.colors.primary[40],
        "--rq-badge-border": theme.colors.neutral[80],
        "--rq-stat-label-color": theme.colors.neutral[50],
        "--rq-stat-value-color": theme.colors.neutral[10],
        "--rq-space-xs": `${theme.spacing.xs}px`,
        "--rq-space-sm": `${theme.spacing.sm}px`,
        "--rq-space-md": `${theme.spacing.md}px`,
        "--rq-space-lg": `${theme.spacing.lg}px`,
        "--rq-space-2xl": `${theme.spacing["2xl"]}px`,
        "--rq-radii-xl": `${theme.radii.xl}px`,
        "--rq-radii-lg": `${theme.radii.lg}px`,
        "--rq-shadow-tonal": theme.shadows.sm,
        "--rq-shadow-badge": theme.shadows.xs,
        "--rq-tonal-btn-size": `${Math.round(theme.spacing.md * 3.5)}px`,
        "--rq-title-fs": titleLarge.fontSize,
        "--rq-title-lh": titleLarge.lineHeight,
        "--rq-title-fw": titleLarge.fontWeight,
        "--rq-title-ff": titleLarge.fontFamily,
        "--rq-title-track": titleLarge.letterSpacing,
        "--rq-badge-fs": labelSmall.fontSize,
        "--rq-badge-lh": labelSmall.lineHeight,
        "--rq-badge-fw": labelSmall.fontWeight,
        "--rq-badge-ff": labelSmall.fontFamily,
        "--rq-badge-track": labelSmall.letterSpacing,
        "--rq-stat-label-fs": bodyMedium.fontSize,
        "--rq-stat-label-lh": bodyMedium.lineHeight,
        "--rq-stat-label-fw": bodyMedium.fontWeight,
        "--rq-stat-label-ff": bodyMedium.fontFamily,
        "--rq-stat-label-track": bodyMedium.letterSpacing,
        "--rq-stat-value-fs": headlineMedium.fontSize,
        "--rq-stat-value-lh": headlineMedium.lineHeight,
        "--rq-stat-value-fw": headlineMedium.fontWeight,
        "--rq-stat-value-ff": headlineMedium.fontFamily,
        "--rq-stat-value-track": headlineMedium.letterSpacing,
        "--rq-feedback-fs": titleMedium.fontSize,
        "--rq-feedback-lh": titleMedium.lineHeight,
        "--rq-feedback-fw": titleMedium.fontWeight,
        "--rq-feedback-ff": titleMedium.fontFamily,
        "--rq-feedback-track": titleMedium.letterSpacing,
        "--rq-scrollbar": theme.colors.neutral[80],
        "--rq-math-fs": bodyLarge.fontSize,
        "--rq-math-lh": bodyLarge.lineHeight,
        "--rq-math-fw": bodyLarge.fontWeight,
        "--rq-math-ff": bodyLarge.fontFamily,
        "--rq-math-track": bodyLarge.letterSpacing,
        "--rq-math-color": theme.colors.neutral[20],
      }) as CSSProperties,
    [
      theme,
      bodyLarge,
      bodyMedium,
      headlineMedium,
      labelSmall,
      titleLarge,
      titleMedium,
    ],
  );

  const { data: videoResponse } = useGetVideoViewSolution(videoId, {
    request: {
      clientName: "cms",
      paramsSerializer: { indexes: null },
    },
    query: {
      enabled:
        questionData.isAttempted === true &&
        videoId !== undefined &&
        videoId !== 0,
      refetchOnMount: true,
      staleTime: 0,
    },
  });

  useEffect(() => {
    const v = (
      videoResponse as {
        video?: {
          videoId: number;
          thumbnail?: string;
          videoURL?: string;
          title?: string;
          duration?: string;
          description?: string;
          type?: { videoTypeName?: string };
          mediaTypeName?: string;
        };
      }
    )?.video;
    if (v) {
      setVideo({
        videoId: v.videoId,
        thumbnail: v.thumbnail ?? "",
        videoURL: v.videoURL ?? "",
        title: v.title ?? "",
        duration: v.duration ?? "",
        description: v.description ?? "",
        typeVideoTypeName: v.type?.videoTypeName ?? "",
        mediaTypeName: v.mediaTypeName ?? "",
      });
    }
  }, [videoResponse]);

  const isDrawer = variant === "drawer";

  return (
    <div
      className={`${styles.overlay} ${isDrawer ? styles.overlayDrawer : ""}`}
      style={rootStyle}
    >
      <header
        className={`${styles.header} ${isDrawer ? styles.headerDrawer : styles.headerOverlay}`}
      >
        {!isDrawer ? (
          <button
            type="button"
            className={styles.backBtnTonal}
            onClick={onBack}
            aria-label={t("learn.exercise.back", { defaultValue: "Back" })}
          >
            <Icon name="arrowBack" size={24} color={theme.colors.primary[40]} />
          </button>
        ) : null}
        <div className={styles.titleGroup}>
          <Icon name="bulbIcon" size={24} color={theme.colors.secondary[10]} />
          <h2 className={styles.title}>{t("learn.exercise.solutions")}</h2>
          {questionData.difficulty_level ? (
            <span className={styles.badge}>
              {questionData.difficulty_level}
            </span>
          ) : null}
        </div>
        {isDrawer ? (
          <button
            type="button"
            className={styles.closeBtnTonal}
            onClick={onBack}
            aria-label={t("reviseQuestions.solution.close")}
          >
            <Icon name="close" size={24} color={theme.colors.primary[40]} />
          </button>
        ) : null}
      </header>

      <div className={styles.scroll}>
        {video?.thumbnail ? (
          <div className={styles.videoWrap}>
            <div className={styles.videoInner}>
              <img className={styles.videoThumb} src={video.thumbnail} alt="" />
              <div className={styles.videoOverlay} />
              <div className={styles.playCenter}>
                <button
                  type="button"
                  className={styles.playBtn}
                  disabled={!video.videoURL}
                  aria-disabled={!video.videoURL}
                  onClick={() => {
                    if (!video.videoURL) return;
                    onOpenVideo(video);
                  }}
                  aria-label={t("playVideo", { defaultValue: "Play video" })}
                >
                  <Icon name="interactivePlayIcon" size={56} />
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <MathJaxHtml
          content={questionData?.question?.solution?.[0]?.data ?? ""}
          className={styles.reviseTextMathStyle}
        />
      </div>

      <footer className={styles.footer}>
        <p className={styles.feedbackText}>
          {t("learn.exercise.solutionHelpful")}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.thumbHit}
            onClick={onLikePress}
            aria-label={t("like", { defaultValue: "Like" })}
          >
            <span className={styles.thumbInner}>
              <Icon
                name={questionData?.isLiked ? "likeActiveSl" : "likeInActiveSl"}
                size={24}
                color={theme.colors.primary[40]}
              />
            </span>
          </button>
          <button
            type="button"
            className={styles.thumbHit}
            onClick={() => {
              onDislikePress();
              if (questionData?.isDisliked !== true) {
                onOpenReportModal("questionFeedback", false);
              }
            }}
            aria-label={t("dislike", { defaultValue: "Dislike" })}
          >
            <span className={styles.thumbInner}>
              <Icon
                name={
                  questionData?.isDisliked
                    ? "disLikeActiveSl"
                    : "disLikeInActiveSl"
                }
                size={24}
                color={theme.colors.primary[40]}
              />
            </span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export const ReviseSolutionWeb = memo(
  ReviseSolutionWebInner,
  (prev, next) =>
    prev.questionData.question_id === next.questionData.question_id &&
    prev.questionData.isLiked === next.questionData.isLiked &&
    prev.questionData.isDisliked === next.questionData.isDisliked &&
    prev.videoId === next.videoId &&
    prev.onBack === next.onBack &&
    prev.onLikePress === next.onLikePress &&
    prev.onDislikePress === next.onDislikePress &&
    prev.onOpenReportModal === next.onOpenReportModal &&
    prev.onOpenVideo === next.onOpenVideo &&
    prev.variant === next.variant,
);
